import deepmerge from 'deepmerge'

export * from './cycleSafe'

export const filterEntries = (filter, object) =>
  Object.fromEntries(Object.entries(object).filter(filter))

export const filterKeys = (filter, object) =>
  filterEntries(([key]) => filter(key), object)

export const filterValues = (filter, object) =>
  filterEntries(([, value]) => filter(value), object)

export const omit = (object, keys = []) =>
  filterKeys(key => !keys.includes(key), object)

export const pick = (object, keys = []) =>
  filterKeys(key => keys.includes(key), object)

export const isObject = object => typeof object === 'object'

export const mapEntries = (
  mapper: ([string, any], index: number) => [string, any],
  object: Record<string, unknown>
) => Object.fromEntries(Array.from(Object.entries(object).map(mapper)))

export const mapKeys = (
  mapper: (key: string, idx: number) => string,
  object: Record<string, unknown>
) =>
  mapEntries(([key, value], idx: number) => [mapper(key, idx), value], object)

export const mapValues = (
  mapper: (value: any, idx: number) => any,
  object: Record<string, unknown>
) =>
  mapEntries(([key, value], idx: number) => [key, mapper(value, idx)], object)

/**
 * Tests if object or array is empty, null or undefined
 */
export const isEmpty = object => {
  // eslint-disable-next-line no-restricted-syntax
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      return false
    }
  }
  return true
}

export const ascending = field => (currObj, nextObj) => {
  // For variants possible scenarios are:
  //      unitQuantityString = '80'
  //      OR
  //      unitQuantityString = '3x0.5 ml'
  // quite handy that `parseInt("3x0.5 mg") // => 3`
  // that means that we don't have to do fancy stuff to
  // figure out value/type of the field, at least for now.
  const currentVal = parseInt(currObj[field], 10)
  const nextVal = parseInt(nextObj[field], 10)

  return currentVal - nextVal
}

export const sort = (collection, field) => {
  if (!collection || !Array.isArray(collection) || !field) {
    return collection
  }

  return [...collection].sort(ascending(field))
}

const defaultCompareFunction = (a, b) => (a < b ? -1 : 1)

export const sortByKey = (object, compare = defaultCompareFunction) =>
  Object.fromEntries(Object.entries(object).sort(([a], [b]) => compare(a, b)))

export const isPrimitiveValue = value => {
  return (
    value == null ||
    typeof value !== 'object' ||
    // See https://developer.mozilla.org/en-US/docs/Glossary/Primitive#Primitive_wrapper_objects_in_JavaScript
    // Note that BigInt and Symbol are not constructable, so there’s no
    // point in listing them here
    value instanceof String ||
    value instanceof Number ||
    value instanceof Boolean
  )
}

/**
 * @param {object} args
 * @param {function?} args.shouldTransform
 * @param {function} args.transform
 * @param {any} obj Input
 */
export const walkOverPropsRecursively = (args, obj) => {
  const shouldTransform = args.shouldTransform || isPrimitiveValue
  if (shouldTransform(obj)) {
    return args.transform(obj)
  }

  if (isPrimitiveValue(obj)) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(val => walkOverPropsRecursively(args, val))
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      walkOverPropsRecursively(args, value)
    ])
  )
}

export const getObjectPrimitiveValues = (obj, filterByKeys = (_, v) => v) =>
  [].concat(
    ...Object.entries(obj).map(([key, val]) =>
      isPrimitiveValue(val)
        ? filterByKeys(key, val)
        : getObjectPrimitiveValues(val, filterByKeys)
    )
  )

// eslint-disable-next-line max-params
export const getPathByValue = (obj, value, path = null) => {
  let resultPath
  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj) {
    if (obj[key]) {
      const objectValue = obj[key]
      if (objectValue === value) {
        return path ? `${path}.${key}` : key
      } else if (isObject(objectValue)) {
        resultPath = getPathByValue(
          objectValue,
          value,
          path ? `${path}.${key}` : key
        )
      }
      if (resultPath) {
        return resultPath
      }
    }
  }
  return null
}

export const getValueByPath = (obj, path = '') => {
  let ptr = obj
  path.split('.').forEach(key => {
    ptr = ptr?.[key]
  })
  return ptr
}

// eslint-disable-next-line max-params
export const setValueByPath = ({ obj, path, value }) => {
  const pathArr = path.split('.')
  const objectToMerge = {}
  let ptr = objectToMerge
  const lastKey = pathArr.pop()
  // eslint-disable-next-line no-unused-expressions
  pathArr.forEach(key => {
    ptr[key] = {}
    ptr = ptr[key]
  })
  ptr[lastKey] = value
  return deepmerge(obj, objectToMerge)
}

// Simplify handling of arrays with IDs in redux by converting them to object
// which have the ids as properties under which the rest of the elements lie.
// This way we can exploit the fact, that javascript objects can only
// have one property of a certain name and we don't have to check
// the store if an array with id X exists etc.
export const objectifyArrayWithIds = (
  arrayWithIds: any[]
): { [key: string]: any } => {
  const finalObject = {}
  if (arrayWithIds?.length > 0) {
    arrayWithIds.forEach(element => {
      const { id } = element
      finalObject[id] = { ...element }
    })
  }
  return finalObject
}
